import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Flex,
  HStack,
  VStack,
  Text,
  Heading,
  Spinner,
  Image,
  IconButton,
  Textarea,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useToast,
} from '@chakra-ui/react';
import {
  FiArrowLeft,
  FiCheck,
  FiX,
  FiGlobe,
  FiDownload,
} from 'react-icons/fi';
import { useReviewerEvent, useEventVerdictMutation } from '../hooks/useReviewerEvent';
import { downloadAttachment } from '../services/api';
import EventPreviewCard from '../components/events/EventPreviewCard';
import {
  EventDetailsPanel,
  ComplianceDisclosuresPanel,
  ComplianceNotesPanel,
  QualificationQuestionsPanel,
  PromotionPanel,
  EventMaterialsPanel,
} from '../components/events/EventDetailsView';

const VERDICT_META = {
  pending: {
    label: 'Pending review',
    color: 'rgba(251,191,36,0.95)',
    bg: 'rgba(251,191,36,0.14)',
    border: 'rgba(251,191,36,0.32)',
  },
  approved: {
    label: 'Approved',
    color: 'rgba(122,226,161,0.95)',
    bg: 'rgba(122,226,161,0.1)',
    border: 'rgba(122,226,161,0.3)',
  },
  rejected: {
    label: 'Rejected',
    color: 'rgba(248,113,113,0.95)',
    bg: 'rgba(248,113,113,0.1)',
    border: 'rgba(248,113,113,0.3)',
  },
  changes_requested: {
    label: 'Changes requested',
    color: 'rgba(251,191,36,0.95)',
    bg: 'rgba(251,191,36,0.1)',
    border: 'rgba(251,191,36,0.3)',
  },
};

// ─── Verdict confirmation modal ───────────────────────────────────────────────
// Note is authored in the right-column panel; this modal only confirms the action.

const EventVerdictModal = ({ isOpen, onClose, verdictType, eventTitle, reviewerNote, onConfirm, isSubmitting }) => {
  const isApprove = verdictType === 'approved';

  const accentColor = isApprove ? 'rgba(74,222,128,0.9)' : 'rgba(248,113,113,0.9)';
  const accentBg = isApprove ? 'rgba(34,197,94,0.10)' : 'rgba(239,68,68,0.10)';
  const accentBorder = isApprove ? 'rgba(34,197,94,0.28)' : 'rgba(239,68,68,0.28)';
  const btnBg = isApprove ? '#22c55e' : '#ef4444';
  const actionLabel = isApprove ? 'Approve event' : 'Reject event';
  const trimmedNote = (reviewerNote || '').trim();

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(3px)" />
      <ModalContent bg="#0f0f0f" border="1px solid rgba(255,255,255,0.10)" borderRadius="12px" color="#fafafa" mx={4}>
        <ModalHeader pb={2}>
          <VStack align="start" spacing={1}>
            <Box px={2} py="2px" borderRadius="full" bg={accentBg} border={`1px solid ${accentBorder}`} alignSelf="flex-start">
              <Text fontSize="10px" fontWeight="700" color={accentColor} letterSpacing="0.06em" textTransform="uppercase">
                {isApprove ? 'Approve' : 'Reject'}
              </Text>
            </Box>
            <Text fontSize="md" fontWeight="600" color="#fafafa">
              {actionLabel}
            </Text>
          </VStack>
        </ModalHeader>
        <ModalBody pb={4}>
          <VStack spacing={3} align="stretch">
            {eventTitle && (
              <Box px={3} py={2} borderRadius="8px" bg="rgba(255,255,255,0.04)" border="1px solid rgba(255,255,255,0.08)">
                <Text fontSize="xs" color="rgba(255,255,255,0.4)" mb={0.5}>Event</Text>
                <Text fontSize="sm" fontWeight="500" color="#fafafa" noOfLines={2}>{eventTitle}</Text>
              </Box>
            )}
            <Box px={3} py={2.5} borderRadius="8px" bg="rgba(255,255,255,0.03)" border="1px solid rgba(255,255,255,0.07)">
              <HStack justify="space-between" mb={1.5}>
                <Text fontSize="xs" color="rgba(255,255,255,0.35)" letterSpacing="0.04em" textTransform="uppercase" fontWeight="500">
                  Reviewer note
                </Text>
                <Text fontSize="xs" color="rgba(255,255,255,0.28)" fontStyle="italic">
                  Visible to the advisor
                </Text>
              </HStack>
              {trimmedNote ? (
                <Text fontSize="sm" color="rgba(255,255,255,0.7)" lineHeight="1.6" whiteSpace="pre-wrap">
                  {trimmedNote}
                </Text>
              ) : (
                <Text fontSize="sm" color="rgba(255,255,255,0.25)" fontStyle="italic">
                  No note added
                </Text>
              )}
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter borderTop="1px solid rgba(255,255,255,0.08)" gap={2}>
          <Button
            size="sm"
            variant="ghost"
            color="rgba(255,255,255,0.5)"
            onClick={onClose}
            isDisabled={isSubmitting}
            borderRadius="full"
            _hover={{ color: 'rgba(255,255,255,0.85)', bg: 'rgba(255,255,255,0.06)' }}
            _focus={{ boxShadow: 'none' }}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            bg={btnBg}
            color="#fff"
            borderRadius="full"
            fontWeight="600"
            fontSize="sm"
            h="32px"
            px={4}
            isLoading={isSubmitting}
            onClick={() => onConfirm(trimmedNote || null)}
            _hover={{ opacity: 0.88 }}
            _focus={{ boxShadow: 'none' }}
          >
            {actionLabel}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// ─── Reviewer note panel (editable) ──────────────────────────────────────────

const ReviewerNotePanel = ({ note, onChange, verdict }) => {
  const hasVerdict = !!verdict?.status;

  const decidedLabel = verdict?.decided_at
    ? new Date(verdict.decided_at).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : null;

  const statusColor =
    verdict?.status === 'approved'
      ? 'rgba(122,226,161,0.9)'
      : verdict?.status === 'rejected'
      ? 'rgba(248,113,113,0.9)'
      : 'rgba(251,191,36,0.9)';

  const statusLabel =
    verdict?.status === 'approved'
      ? 'Approved'
      : verdict?.status === 'rejected'
      ? 'Rejected'
      : verdict?.status === 'changes_requested'
      ? 'Changes requested'
      : null;

  return (
    <Box mt={8} mb={2}>
      <HStack justify="space-between" mb={3}>
        <Text
          fontSize="10px"
          fontWeight="700"
          color="rgba(255,255,255,0.35)"
          letterSpacing="0.08em"
          textTransform="uppercase"
        >
          Reviewer note
        </Text>
        {hasVerdict && (
          <HStack spacing={2}>
            <Box
              px={2}
              py="2px"
              borderRadius="full"
              bg="rgba(255,255,255,0.05)"
              border="1px solid rgba(255,255,255,0.10)"
            >
              <Text
                fontSize="10px"
                fontWeight="700"
                color={statusColor}
                letterSpacing="0.05em"
                textTransform="uppercase"
              >
                {statusLabel}
              </Text>
            </Box>
            {decidedLabel && (
              <Text fontSize="xs" color="rgba(255,255,255,0.35)">
                {decidedLabel}
              </Text>
            )}
          </HStack>
        )}
      </HStack>

      <Textarea
        value={note}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Add a note for the advisor — they will see this when notified of your decision…"
        bg="rgba(255,255,255,0.02)"
        color="#fafafa"
        borderColor="rgba(255,255,255,0.08)"
        borderRadius="10px"
        fontSize="sm"
        rows={4}
        resize="vertical"
        _placeholder={{ color: 'rgba(255,255,255,0.22)' }}
        _hover={{ borderColor: 'rgba(255,255,255,0.16)' }}
        _focus={{ borderColor: 'rgba(255,255,255,0.28)', boxShadow: 'none', bg: 'rgba(255,255,255,0.03)' }}
      />
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const EventReviewPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const { data: event, isLoading, error } = useReviewerEvent(eventId);
  const verdictMutation = useEventVerdictMutation(eventId);

  // null | 'approved' | 'rejected' — which verdict the confirm modal is open for.
  const [verdictModal, setVerdictModal] = useState(null);
  // The full cover-photo src for the lightbox (null = closed).
  const [coverPhoto, setCoverPhoto] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  // Reviewer note — seeded from any saved verdict note, editable in the right panel.
  const [reviewerNote, setReviewerNote] = useState('');

  // Seed the note field once the event loads (or when a verdict is saved).
  useEffect(() => {
    setReviewerNote(event?.my_verdict?.note || '');
  }, [event?.my_verdict?.note]);

  const currentStatus = event?.complianceStatus || null;
  // The header chip mirrors compliance state: pending until a verdict lands,
  // then turns into Approved/Rejected/Changes requested.
  const statusKey = currentStatus && VERDICT_META[currentStatus] ? currentStatus : 'pending';

  const ownerName = event?.owner_display_name || 'A GillyReach user';
  const ownerEmail = event?.owner_email || '';
  const contextNote = event?.advisor_context_note || null;

  const previewData = useMemo(() => event || {}, [event]);

  const openVerdict = (status) => setVerdictModal(status);

  // Download the whole record (details + promotion + compliance + appended
  // attachments) as one PDF. The server assembles it; we just save the blob.
  const handleDownloadPdf = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      const filename = `${(event?.title || 'event').trim() || 'event'}-review.pdf`;
      await downloadAttachment(`/api/event-reviews/events/${eventId}/export`, filename);
    } catch (err) {
      toast({
        title: "Couldn't download the PDF",
        description: err?.response?.data?.detail || err?.message || 'Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleVerdictConfirm = () => {
    const status = verdictModal;
    const note = reviewerNote.trim() || null;
    verdictMutation.mutate({ status, note });
    setVerdictModal(null);
    toast({
      title: status === 'approved' ? 'Event approved' : 'Event rejected',
      description: 'The event owner has been notified.',
      status: 'success',
      duration: 4000,
      isClosable: true,
    });
  };

  if (isLoading) {
    return (
      <Flex align="center" justify="center" minH="60vh">
        <Spinner size="md" color="rgba(255,255,255,0.5)" thickness="2px" />
      </Flex>
    );
  }

  if (error || !event) {
    // 410 = the advisor reopened the event to edit it, so this link was
    // superseded. Soften the copy and hint that a fresh link may follow.
    const wasSuperseded = error?.response?.status === 410;
    return (
      <Flex align="center" justify="center" minH="60vh" px={4}>
        <VStack spacing={4} maxW="md" textAlign="center">
          <Heading size="md" color="#fafafa">
            {wasSuperseded ? 'This review link is no longer active' : 'Event not found'}
          </Heading>
          <Text fontSize="sm" color="#a3a3a3">
            {wasSuperseded
              ? 'The advisor reopened this event to make changes, so the version you were asked to review no longer applies. If they still need your review, a fresh link will arrive by email.'
              : 'This event may have been deleted, or your access was revoked. Ask the advisor to re-send the invite.'}
          </Text>
          <Button
            onClick={() => navigate('/?view=events&tab=reviews')}
            bg="#fafafa"
            color="#0a0a0a"
            borderRadius="full"
            fontSize="sm"
            fontWeight="600"
            _hover={{ opacity: 0.85 }}
          >
            Back to shared events
          </Button>
        </VStack>
      </Flex>
    );
  }

  const submitting = verdictMutation.isLoading;

  return (
    <Flex direction="column" h="calc(100vh - 64px)" minH="0" w="100%">

      {/* ── Toolbar: sticky, never scrolls ───────────────────────────────── */}
      <Box
        flexShrink={0}
        pl={3}
        pr={2}
        py={1}
        borderBottom="1px solid"
        borderColor="rgba(255,255,255,0.08)"
        bg="#0a0a0a"
      >
        <HStack justify="space-between" align="center" spacing={1}>
          <HStack spacing={1}>
            <IconButton
              icon={<FiArrowLeft size={16} />}
              aria-label="Back to shared events"
              size="xs"
              variant="ghost"
              color="gray.400"
              height="28px"
              minW="28px"
              borderRadius="6px"
              _hover={{ color: 'rgba(255,255,255,0.7)', bg: 'rgba(255,255,255,0.06)' }}
              _active={{ bg: 'rgba(255,255,255,0.08)' }}
              _focus={{ boxShadow: 'none' }}
              onClick={() => navigate('/?view=events&tab=reviews')}
            />
            {VERDICT_META[statusKey] && (
              <Box
                px={2}
                py="2px"
                borderRadius="full"
                bg={VERDICT_META[statusKey].bg}
                border={`1px solid ${VERDICT_META[statusKey].border}`}
              >
                <Text fontSize="10px" fontWeight="700" color={VERDICT_META[statusKey].color} letterSpacing="0.06em" textTransform="uppercase">
                  {VERDICT_META[statusKey].label}
                </Text>
              </Box>
            )}
          </HStack>

          <HStack spacing={1.5} align="center">
            <Tooltip label="Download full record as PDF" placement="bottom" openDelay={300} bg="rgba(20,20,20,0.95)" color="rgba(255,255,255,0.85)" fontSize="xs" borderRadius="6px" px={2} py={1}>
              <IconButton icon={<FiDownload size={16} />} aria-label="Download full record as PDF" size="xs" variant="ghost" color="rgba(255,255,255,0.45)" height="28px" minW="28px" borderRadius="6px" isLoading={isDownloading} onClick={handleDownloadPdf} _hover={{ color: 'rgba(255,255,255,0.85)', bg: 'rgba(255,255,255,0.06)' }} _active={{ bg: 'rgba(255,255,255,0.08)' }} _focus={{ boxShadow: 'none' }} />
            </Tooltip>
            <Tooltip label={currentStatus === 'approved' ? 'Already approved — click to change' : 'Approve this event'} placement="bottom" openDelay={300} bg="rgba(20,20,20,0.95)" color="rgba(255,255,255,0.85)" fontSize="xs" borderRadius="6px" px={2} py={1}>
              <IconButton icon={<FiCheck size={16} />} aria-label="Approve event" size="xs" variant="ghost" color={currentStatus === 'approved' ? 'rgba(122,226,161,0.85)' : 'rgba(255,255,255,0.45)'} height="28px" minW="28px" borderRadius="6px" isDisabled={submitting} onClick={() => openVerdict('approved')} _hover={{ color: 'rgba(122,226,161,0.9)', bg: 'rgba(34,197,94,0.08)' }} _active={{ bg: 'rgba(34,197,94,0.12)' }} _focus={{ boxShadow: 'none' }} />
            </Tooltip>
            <Tooltip label={currentStatus === 'rejected' ? 'Already rejected — click to change' : 'Reject this event'} placement="bottom" openDelay={300} bg="rgba(20,20,20,0.95)" color="rgba(255,255,255,0.85)" fontSize="xs" borderRadius="6px" px={2} py={1}>
              <IconButton icon={<FiX size={16} />} aria-label="Reject event" size="xs" variant="ghost" color={currentStatus === 'rejected' ? 'rgba(248,113,113,0.85)' : 'rgba(255,255,255,0.45)'} height="28px" minW="28px" borderRadius="6px" isDisabled={submitting} onClick={() => openVerdict('rejected')} _hover={{ color: 'rgba(248,113,113,0.9)', bg: 'rgba(239,68,68,0.08)' }} _active={{ bg: 'rgba(239,68,68,0.12)' }} _focus={{ boxShadow: 'none' }} />
            </Tooltip>
          </HStack>
        </HStack>
      </Box>

      {/* ── Meta strip: sleek single-line context, just below the buttons ── */}
      <Box
        flexShrink={0}
        px={{ base: 4, md: 6 }}
        py={3}
        bg="#0a0a0a"
      >
        <HStack
          spacing={2}
          fontSize="xs"
          color="rgba(255,255,255,0.55)"
          flexWrap="wrap"
          rowGap={1}
        >
          <Text color="rgba(255,255,255,0.85)" fontWeight="600">
            Compliance review
          </Text>
          <Text color="rgba(255,255,255,0.25)">·</Text>
          <Text>
            Requested by{' '}
            <Text as="span" color="rgba(255,255,255,0.85)" fontWeight="500">
              {ownerName}
            </Text>
          </Text>
          {ownerEmail && (
            <>
              <Text color="rgba(255,255,255,0.25)">·</Text>
              <Text>{ownerEmail}</Text>
            </>
          )}
          {contextNote && (
            <>
              <Text color="rgba(255,255,255,0.25)">·</Text>
              <Text color="rgba(255,255,255,0.7)" fontStyle="italic" noOfLines={1}>
                “{contextNote}”
              </Text>
            </>
          )}
        </HStack>
      </Box>

      {/* ── Publishing intent banner ─────────────────────────────────────── */}
      <Box
        flexShrink={0}
        px={{ base: 4, md: 6 }}
        py={3}
        bg="rgba(99,179,237,0.07)"
        borderTop="1px solid rgba(99,179,237,0.15)"
        borderBottom="1px solid rgba(99,179,237,0.15)"
      >
        <HStack spacing={2} mb={1.5}>
          <Box color="rgba(99,179,237,0.8)" flexShrink={0} display="flex" alignItems="center">
            <FiGlobe size={13} />
          </Box>
          <Text fontSize="xs" fontWeight="600" color="rgba(99,179,237,0.95)">
            What you approve is what attendees see, published as-is
          </Text>
        </HStack>
        <Text fontSize="xs" color="rgba(255,255,255,0.48)" lineHeight="1.7" pl="21px">
          Once approved, this event is promoted on public event registration and ticketing platforms using this information exactly. The title, description, cover photo, pricing, and all details go live without modification. The GillyReach regulatory disclosure is automatically appended to the event description prior to publishing.
        </Text>
      </Box>

      {/* ── Two-column body: full width, fills remaining height ──────────── */}
      <Flex flex={1} minH={0} w="100%" overflow="hidden">

        {/* LEFT — attendee preview card */}
        <Box
          w="420px"
          flexShrink={0}
          borderRight="1px solid rgba(255,255,255,0.07)"
          px={6}
          py={6}
          overflowY="auto"
        >
          <Text
            fontSize="10px"
            fontWeight="700"
            color="rgba(255,255,255,0.22)"
            letterSpacing="0.09em"
            textTransform="uppercase"
            mb={3}
            textAlign="center"
          >
            Typical attendee view · layouts vary by platform
          </Text>
          <EventPreviewCard data={previewData} mode="review" />
        </Box>

        {/* RIGHT — full event details + reviewer note (scroll together) */}
        <Box flex={1} px={{ base: 6, lg: 8 }} py={6} overflowY="auto">
          <EventDetailsPanel event={event} onViewCover={setCoverPhoto} />

          <QualificationQuestionsPanel questions={event.qualificationQuestions} />

          <ComplianceDisclosuresPanel
            disclosures={event.complianceDisclosures}
            pricingType={event.pricingType}
            totalEventValue={event.totalEventValue}
          />

          <PromotionPanel
            socialPosts={event.socialPosts}
            promotionFlyers={event.promotionFlyers}
          />

          <ComplianceNotesPanel notes={event.complianceNotes} />

          <EventMaterialsPanel attachments={event.complianceAttachments} />

          <ReviewerNotePanel
            note={reviewerNote}
            onChange={setReviewerNote}
            verdict={event.my_verdict}
          />

        </Box>

      </Flex>

      <EventVerdictModal
        isOpen={verdictModal !== null}
        onClose={() => !submitting && setVerdictModal(null)}
        verdictType={verdictModal}
        eventTitle={event?.title}
        reviewerNote={reviewerNote}
        onConfirm={handleVerdictConfirm}
        isSubmitting={submitting}
      />

      {/* Cover photo lightbox — shows the whole, uncropped image (contain). */}
      <Modal isOpen={!!coverPhoto} onClose={() => setCoverPhoto(null)} isCentered size="4xl">
        <ModalOverlay bg="rgba(0,0,0,0.85)" backdropFilter="blur(4px)" />
        <ModalContent bg="transparent" boxShadow="none" mx={4} my="auto">
          <ModalCloseButton
            color="#fafafa"
            bg="rgba(0,0,0,0.45)"
            borderRadius="full"
            zIndex={2}
            _hover={{ bg: 'rgba(0,0,0,0.7)' }}
            _focus={{ boxShadow: 'none' }}
          />
          <Flex align="center" justify="center">
            <Image
              src={coverPhoto || undefined}
              alt="Event cover — full size"
              maxH="85vh"
              maxW="100%"
              objectFit="contain"
              borderRadius="10px"
            />
          </Flex>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default EventReviewPage;
